import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { environment } from '@environments/environment';
import { ApiResponse } from '@app/_models';
import { Report } from '@app/_components/report';
import { retry, catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { AlertService } from '@app/_services';
import { CellClassRules } from 'ag-grid-enterprise';
import * as moment from 'moment';
import { SharedService } from './../_shared/shared.service';

@Injectable({ providedIn: 'root' })
export class ReportService {
	constructor
		(
			private http: HttpClient,
			private alertService: AlertService,
			private sharedService: SharedService
		) { }

	urlEndpoint = 'reports';

	//Show columns dynamically in Future occupancy report
	futureOccupancyColumns: any = [
		{ value: 'revRoomRateNetTTL', name: 'revRoomRateNetTTL' },
		{ value: 'revRoomRateNetAVG', name: 'revRoomRateNetAVG' },
		{ value: 'ownerAndOwnerGuestNts', name: 'ownerAndOwnerGuestNts' },
		{ value: 'numNtsMaintTTL', name: 'numNtsMaintTTL' },
		{ value: 'totalOccupancy', name: 'totalOccupancy' },
		{ value: 'guestOccupancy', name: 'guestOccupancy' },
		{ value: 'nonGuestOccupancy', name: 'nonGuestOccupancy' },
	];


	// ag-grid columns
	occupancyNtsCellClassRule: CellClassRules = {
		'green90': (params) => params.value > 30,
		'green60': (params) => params.value > 28 && params.value <= 30,
		'green30': (params) => params.value > 26 && params.value <= 28,
		'yellow30': (params) => params.value > 24 && params.value <= 26,
		'yellow60': (params) => params.value > 22 && params.value <= 24,
		'yellow90': (params) => params.value > 20 && params.value <= 22,
		'red30': (params) => params.value > 18 && params.value <= 20,
		'red60': (params) => params.value > 16 && params.value <= 18,
		'red90': (params) => params.value <= 16,
	};

	occupancyPctCellClassRule: CellClassRules = {
		'green90': (params) => params.value > .93,
		'green60': (params) => params.value > .87 && params.value <= .93,
		'green30': (params) => params.value > .83 && params.value <= .87,
		'yellow30': (params) => params.value > .77 && params.value <= .83,
		'yellow60': (params) => params.value > .70 && params.value <= .77,
		'yellow90': (params) => params.value > .63 && params.value <= .70,
		'red30': (params) => params.value > .57 && params.value <= .63,
		'red60': (params) => params.value > .50 && params.value <= .57,
		'red90': (params) => params.value <= .5,
	};

	/*
	RESERVATION_STATUS_QUOTE_HOLD
	RESERVATION_STATUS_SALE_PENDING
	RESERVATION_STATUS_SALE_CLOSED
	RESERVATION_STATUS_NOT_AVAILABLE
	RESERVATION_STATUS_OWNER_OCCUPIED
	RESERVATION_STATUS_OWNER_GUEST_OCCUPIED
	*/
	activeReservations: any = ['QH', 'OP', 'OC', 'NA', 'OO', 'OG'];

	revanueReservations: any = ['OP', 'NA', 'OO', 'OG'];

	getRentedPercent(params: any) {
		return (params?.data?.numNightsTTL / params?.data?.numNtsInMonthTTL)
	}


	getNtsAvailToRent(params: any) {
		let ntsAvailToRent = new Date(moment(params.data.date).year(), moment(params.data.date).month(), 0).getDate() - (params.data.numNtsOwnerTTL + params.data.numNtsOwnerGuestTTL + params.data.numNtsMaintTTL);
		console.log('ntsAvailToRent' + ntsAvailToRent);
		return ntsAvailToRent;
	}


	getDaysInMonth(month: number, year: number) {
		console.log('days ' + new Date(year, month, 0).getDate());
		return new Date(year, month, 0).getDate();
	}


	getYearFromDate(params: any) {
		return moment(params.data.date).format('YYYY')
	}


	getMonthFromDate(params: any) {
		return moment(params.data.date).format('MMM');
	}

	getYearFromSaleDate(params: any) {
		return moment(params.data.saleDate).format('YYYY')
	}


	getMonthFromSaleDate(params: any) {
		return moment(params.data.saleDate).format('MMM');
	}

	getDayFromSaleDate(params: any) {
		return moment(params.data.saleDate).format('DD');
	}


	getGuestName(params: any) {
		return params.data.reservationId + ' ' + params.data.guestLastName;
	}


	currencyCellRendererUSD(params: any) {
		let inrFormat = new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
			minimumFractionDigits: 0,
			maximumFractionDigits: 0
		});
		return inrFormat.format(params.value);
	}


	percentCellRenderer(params: any) {
		let inrFormat = new Intl.NumberFormat('en-US', {
			style: 'percent',
			minimumFractionDigits: 1,
			maximumFractionDigits: 1
		});
		return inrFormat.format(params.value);
	}


	// API CALLS
	add(report: Report): Observable<ApiResponse> {
		console.log('posting... ' + JSON.stringify(report));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, report);
	}

	update(report: Report): Observable<ApiResponse> {
		return this.http.patch<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, report);
	}

	delete(reportId: number): Observable<ApiResponse> {
		return this.http.delete<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${reportId}`);
	}

	findAll(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	findByFilter(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any): Observable<ApiResponse> {
		let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));
	}

	getResAdrReport(reportSearchApi: any): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/res-adr-report`, reportSearchApi);
	}

	findById(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	audit(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}/audit`).pipe(retry({ count: 2, delay: 1500 }));
	}

	futureVacancyByProperty(shortTermRentalId: String): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/ops/future-vacancy-by-property?shortTermRentalId=${shortTermRentalId}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	/*futureVacancyByDate(utcSearchDate: String): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/ops/future-vacancy-by-date?searchDate=${utcSearchDate}`).pipe(retry({ count: 2, delay: 1500 }));
	}*/

	futureVacancyBySearch(reportSearchApi: any): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/ops/future-vacancy-by-date`, reportSearchApi);
	}

	propertySummaryList(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/property/summary-list`).pipe(retry({ count: 2, delay: 1500 }));
	}

	propertySummaryListYieldMax(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.yieldMaxApiUrl}/properties/summary-list`).pipe(retry({ count: 2, delay: 1500 }));
	}

	//Search developements
	developmentSummaryListYieldMax(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.yieldMaxApiUrl}/developments/summary-list`).pipe(retry({ count: 2, delay: 1500 }));
	}

	rateBucketListYieldMax(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.yieldMaxApiUrl}/rateBuckets`).pipe();
	}

	developmentSummaryList(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/development/summary-list`).pipe(retry({ count: 2, delay: 1500 }));
	}

	teamSummaryList(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/team/summary-list`).pipe(retry({ count: 2, delay: 1500 }));
	}

	userSummaryList(userRoleIdList): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/user/summary-list?userRoleIdList=${userRoleIdList}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	tagSummaryList(tagType): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/tags/summary-list?tagType=${tagType}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	findTagsWith(queryString: string, taskId: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/tags/find-with?query=${queryString}&taskId=${taskId}`).pipe(retry({ count: 2, delay: 1500 }));
	}
	findTagsWithTagType(tagType: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/tags/find-by-tagtype?tagType=${tagType}`).pipe(retry({ count: 2, delay: 1500 }));
	}
	findTagsWithSource(queryString: string, taskId: number, fetchBySourceId: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/tags/find-with-source?query=${queryString}&taskId=${taskId}&fetchBySourceId=${fetchBySourceId}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	findTagsBySource(source: number, sourceId: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/tag-joins/${source}/${sourceId}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	findTagsNotAssignedToTask(queryString: string, taskId: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/tags/find-unassigned?query=${queryString}&taskId=${taskId}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	arrivalSummaryReport(reportSearchApi: any): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/reservations/check-in-out-report`, reportSearchApi);
	}

	reservationRevenueReport(reportSearchApi: any): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/revenue/reservations`, reportSearchApi);
  }

  salesByPropertyReport(reportSearchApi: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/revenue/reservations/sales-by-property`, reportSearchApi);
  }

  /**
   * Sales Pipeline Analytics dashboard — KPIs, daily trend, status mix, and
   * breakdowns (property / reservationist / source / discount tier / bedroom)
   * for Quotes, Holds, and Upsell Extension Offers.
   * Backed by QuoteAnalyticsReportBL on the server.
   *
   * @since 2.6 (2026-05-07)
   */
  quoteAnalyticsReport(reportSearchApi: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/quote/analytics`, reportSearchApi);
  }

  travelInsuranceReport(reportSearchApi: any): Observable<ApiResponse> {
    console.log("reportSearchApi in service", reportSearchApi);
    return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/travelInsurance`, reportSearchApi);
  }

	resMasterReport(reportSearchApi: any): Observable<any> {
		return this.http.post(`${environment.apiUrl}/${this.urlEndpoint}/ledger/reservationSalesReport/exportCSV`, reportSearchApi, {
			responseType: 'text' as 'json'  // force HttpClient to treat CSV as text
		}).pipe();
	}

	reservationChannelsReport(reportSearchApi: any): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/reservations/channels`, reportSearchApi);
	}

	bizDevReport(reportSearchApi: any): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/biz-dev/property`, reportSearchApi);
	}

	occupancyReport(reportSearchApi: any): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/occupancy/occupancy-details`, reportSearchApi);
	}

	futureOccupancyReport(reportSearchApi: any): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/occupancy/future-occupancy`, reportSearchApi);
	}

	// Operations Dashboard — full payload for a date range + optional location + optional WoW compare.
	// All params are optional; backend defaults to today (HST) for single-day calls.
	// No retry — same reasoning as operationsDashboardSection (silent retries blow
	// out the connection pool and starve user-triggered actions like drill-downs).
	operationsDashboard(opts?: { startDate?: Date; endDate?: Date; locationId?: number | null; compareWow?: boolean }): Observable<ApiResponse> {
		const o = opts || {};
		const params: string[] = [];
		if (o.startDate) params.push(`startDate=${moment(o.startDate).format('YYYY-MM-DD')}`);
		if (o.endDate)   params.push(`endDate=${moment(o.endDate).format('YYYY-MM-DD')}`);
		if (o.locationId != null) params.push(`locationId=${o.locationId}`);
		if (o.compareWow) params.push(`compareWow=true`);
		const qs = params.length ? `?${params.join('&')}` : '';
		return this.http.get<ApiResponse>(`${environment.apiUrl}/operations-dashboard${qs}`);
	}

	/**
	 * Operations Dashboard — one section at a time. Hits the new
	 * GET /api/2.6/operations-dashboard/sections?names=... endpoint so the
	 * frontend can fire one HTTP request per tile in parallel instead of waiting
	 * on the all-in-one aggregator.
	 *
	 * Pass a single name like `'kpis'` or an array like `['kpis','equipment']`
	 * if you want to bundle a few. Omitting names returns every section
	 * (drop-in equivalent of operationsDashboard).
	 */
	// IMPORTANT: no retry on this call. Earlier the .pipe(retry({count:2,delay:1500}))
	// here was the root cause of the dashboard saturating the browser's HTTP/1.1
	// connection pool — any slow or transient-error section silently fired up to
	// 3 times (initial + 2 retries with 1.5s gaps), holding connection slots and
	// starving user actions like opening a drill-down. 14 sections × possibly-3
	// retries each = up to 42 in-flight requests; the browser's 6-slot limit
	// queued the rest for tens of seconds. Sections now fail fast and surface
	// inline via sectionError[name] in the component.
	operationsDashboardSection(name: string | string[], opts?: { startDate?: Date; endDate?: Date; locationId?: number | null; compareWow?: boolean }): Observable<ApiResponse> {
		const o = opts || {};
		const names = Array.isArray(name) ? name.join(',') : name;
		const params: string[] = [];
		if (names) params.push(`names=${encodeURIComponent(names)}`);
		if (o.startDate) params.push(`startDate=${moment(o.startDate).format('YYYY-MM-DD')}`);
		if (o.endDate)   params.push(`endDate=${moment(o.endDate).format('YYYY-MM-DD')}`);
		if (o.locationId != null) params.push(`locationId=${o.locationId}`);
		if (o.compareWow) params.push(`compareWow=true`);
		const qs = params.length ? `?${params.join('&')}` : '';
		return this.http.get<ApiResponse>(`${environment.apiUrl}/operations-dashboard/sections${qs}`);
	}

	// Operations Dashboard drill-down — row-level records that feed a single KPI.
	// No retry: drilldowns are user-triggered; a transient failure should surface
	// fast in the dialog rather than silently spin for 5+ seconds before the
	// error finally renders.
	operationsDashboardDrilldown(metric: string, opts?: { startDate?: Date; endDate?: Date; locationId?: number | null }): Observable<ApiResponse> {
		const o = opts || {};
		const params: string[] = [`metric=${encodeURIComponent(metric)}`];
		if (o.startDate) params.push(`startDate=${moment(o.startDate).format('YYYY-MM-DD')}`);
		if (o.endDate)   params.push(`endDate=${moment(o.endDate).format('YYYY-MM-DD')}`);
		if (o.locationId != null) params.push(`locationId=${o.locationId}`);
		return this.http.get<ApiResponse>(`${environment.apiUrl}/operations-dashboard/drilldown?${params.join('&')}`);
	}

	tagRevenueReport(reportSearchApi: any): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/tag-revenue`, reportSearchApi);
	}

	tagRevenueSelectableTags(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/tag-revenue/tags`).pipe(retry({ count: 2, delay: 1500 }));
	}

	tagRevenueSelectableOrderProducts(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/tag-revenue/order-products`).pipe(retry({ count: 2, delay: 1500 }));
	}

	promotionOfferDashboard(reportSearchApi: any): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/promotion-offers/dashboard`, reportSearchApi);
	}

	getReservationSummaryReport(startDate: string, endDate: string, format: 'json' | 'excel' = 'json'): Observable<any> {
		let params = new HttpParams()
			.append('startDate', startDate)
			.append('endDate', endDate)
			.append('format', format);

		if (format === 'excel') {
			return this.http.get(`${environment.apiUrl}/reservations/summary-report`, {
				params: params,
				responseType: 'blob'
			}).pipe(retry({ count: 2, delay: 1500 }));
		}

		return this.http.get<ApiResponse>(`${environment.apiUrl}/reservations/summary-report`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));
	}

	getPaymentSummaryReport(startDate: string, endDate: string, orderId?: number, format: 'json' | 'excel' = 'json'): Observable<any> {
		let params = new HttpParams()
			.append('startDate', startDate)
			.append('endDate', endDate)
			.append('format', format);

		if (orderId) {
			params = params.append('orderId', orderId.toString());
		}

		if (format === 'excel') {
			return this.http.get(`${environment.apiUrl}/reservations/payment-summary-report`, {
				params: params,
				responseType: 'blob'
			}).pipe(retry({ count: 2, delay: 1500 }));
		}

		return this.http.get<ApiResponse>(`${environment.apiUrl}/reservations/payment-summary-report`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));
	}


	prepareParamsFromPrimeNgDataTable(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any) {
		try {
			console.log(
				'  |  page:' + page
				+ '  |  size:' + size
				+ '  |  sortBy:' + sortBy
				+ '  |  sortOrder:' + sortOrder
				+ '  |  multiSortMeta:' + JSON.stringify(multiSortMeta)
				+ '  |  globalFilter:' + globalFilter
				+ '  |  filters:' + JSON.stringify(filters)
			);

			let params = new HttpParams();

			if (!isNaN(page)) { params = params.append('page', page); }
			if (!isNaN(size)) { params = params.append('size', size); }
			if (sortBy) { params = params.append('sort', sortBy + ',' + (sortOrder === 1 ? 'asc' : 'desc')); }
			if (multiSortMeta) {
				// if sortMode="multiple" in primeng table then multiSortMeta is used for ALL sorting (even just one column.)
				// Otherwise, single sorting is used above.
				// event.multiSortMeta:[{"field":"lastName","order":1},{"field":"firstName","order":1}]
				for (let i = 0; i < multiSortMeta.length; i++) {
					let json = JSON.parse(JSON.stringify(multiSortMeta[i]));
					params = params.append('sort', json.field + ',' + (json.order === 1 ? 'asc' : 'desc'));
				}
			}
			// globalFilter works, but did not implemnt in Spring but not hard to write custom findAll and specify certain columns to look.
			if (globalFilter) { params = params.append('filter', globalFilter); }

			// multiselect or input or whatever... these work slick.
			if (filters) {
				// key:status val:{"value":["A"],"matchMode":"in"}
				for (let i = 0, keys = Object.keys(filters), ii = keys.length; i < ii; i++) {
					console.log('key:' + keys[i] + ' val:' + JSON.stringify(filters[keys[i]]));
					params = params.append(keys[i], filters[keys[i]].value);
				}
			}
			return params;
		} catch (error) {
			console.log('prepareParamsFromPrimeNgDataTable()' + error);
		}
	}
}
